import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { loadStripe } from '@stripe/stripe-js';
import { useLocation } from '@reach/router';
import cx from 'classnames';

import { useScrollFreeze } from '@hooks';
import { useSiteConfig } from '@querys/useSiteConfig';
import { PortableTextBlock } from '@modules/portableTextBlock';
import { ArrowButton } from '@components/ui/buttons';

import { Arrow } from '@svg/arrow';
import Close from '@svg/close.svg';

const searchVars = {
  open: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  },
  closed: {
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  },
};

const stripePromise = loadStripe(process.env.GATSBY_STRIPE_PUBLIC_KEY);

const titles = ['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof'];

const NavInner = ({ toggleDonate }) => {
  const [isLoading, setLoading] = useState(false);
  useScrollFreeze();
  const text = useSiteConfig();
  const { donations } = text || {};
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm();

  const { href } = useLocation();

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const { sessionId } = await fetch('/.netlify/functions/create-donation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, returnUrl: href }),
      }).then((res) => res.json());

      const stripe = await stripePromise;

      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });
    } catch (error) {
      setLoading(false);
      console.log(error);
      return Promise.reject(error);
    }
  };

  return (
    <motion.div
      variants={searchVars}
      initial='closed'
      animate='open'
      exit='closed'
      className={cx(
        'z-50 bg-black w-screen h-screen fixed top-0 left-0 items-start justify-start flex flex-col px-gutter overflow-auto pb-20 lg:px-0 lg:grid lg:grid-cols-14 md:items-center md:justify-center',
        {
          'cursor-wait': isLoading,
        },
      )}>
      <div className='col-start-2 col-end-14 lg:col-end-7 prose md:prose-lg text-white lg:pr-halfGutter'>
        <PortableTextBlock text={donations} />
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cx(
          'col-start-2 col-end-14 lg:col-start-8 lg:pl-halfGutter w-full mt-10 lg:mt-0 text-white',
          {
            'cursor-wait': isLoading,
          },
        )}>
        <span className='font-medium text-3xl leading-3xl text-white'>
          Donation amount
        </span>
        <div className='border-b border-white pb-2 flex flex-row items-center mt-4 donate mb-10'>
          <span className='text-grey mr-2'>$</span>
          <Controller
            name='donationAmount'
            control={control}
            rules={{ max: 1000000, required: true }}
            render={({ field: { onChange, onBlur, name, value, ref } }) => (
              <NumberFormat
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale
                onValueChange={(values) => onChange(values.floatValue)}
                name={name}
                value={value}
                onBlur={onBlur}
                ref={ref}
                className='w-full text-base leading-base bg-black text-white focus:outline-none'
                placeholder='Enter donation amount'
              />
            )}
          />
        </div>
        <span className='font-medium text-2xl leading-3xl text-white mb-4 flex'>
          Personal Details
        </span>
        <div className='mt-12 md:mt-0 flex flex-col md:flex-row gap-x-10 items-start'>
          <div className='styled-select reversed w-full md:w-[unset]'>
            <select
              {...register('customerDetails.title')}
              className='styled-input !pt-0 w-full md:w-32'>
              {titles.map((item) => (
                <option value={item}>{item}</option>
              ))}
            </select>
          </div>
          <div className='flex-grow pt-5 md:pt-0 w-full'>
            <input
              type='text'
              className='styled-input reversed w-full !pt-0'
              placeholder='Full Name *'
              {...register('customerDetails.name', { required: true })}
            />
            {errors.name && errors.name.type === 'required' && (
              <span className='error-message'>This is required</span>
            )}
          </div>
        </div>
        {/* Email */}
        <div>
          <input
            type='email'
            className='styled-input reversed w-full'
            placeholder='Email Address *'
            {...register('customerDetails.email', {
              required: true,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              },
            })}
          />
          {errors.email && errors.email.type === 'required' && (
            <span className='error-message'>This is required</span>
          )}
          {errors.email && errors.email.type === 'pattern' && (
            <span className='error-message'>Invalid email address</span>
          )}
        </div>
        {/* Address */}
        <div>
          <input
            type='address'
            className='styled-input reversed w-full'
            placeholder='Address *'
            {...register('customerDetails.address', {
              required: true,
            })}
          />
          {errors.address && errors.address.type === 'required' && (
            <span className='error-message'>This is required</span>
          )}
        </div>
        {/* Postcode */}
        <div>
          <input
            type='postcode'
            className='styled-input w-full'
            placeholder='Postcode *'
            {...register('postcode', {
              required: true,
            })}
          />
          {errors.postcode && errors.postcode.type === 'required' && (
            <span className='error-message'>This is required</span>
          )}
        </div>
        {/* Phone */}
        <div>
          <input
            type='text'
            className='styled-input reversed w-full'
            placeholder='Phone *'
            {...register('customerDetails.phone', {
              required: true,
            })}
          />
          {errors.phone && errors.phone.type === 'required' && (
            <span className='error-message'>This is required</span>
          )}
        </div>
        {errors.donationAmount && (
          <span className='text-red mt-4 block'>This field is required</span>
        )}

        <div className='flex items-center mt-10 gap-x-4'>
          <input
            id='anon'
            type='checkbox'
            className=''
            placeholder='I would like to remain anonymous'
            {...register('customerDetails.anon', {
              required: false,
            })}
          />
          <label for='anon'>I would like to remain anonymous</label>
        </div>

        <ArrowButton
          type='submit'
          isSmall
          className={cx('mt-10 flex w-full justify-end', {
            'cursor-wait': isLoading,
          })}>
          Make Donation
        </ArrowButton>
      </form>

      <button
        onClick={toggleDonate}
        className='absolute top-0 lg:top-5 right-gutter lg:right-halfGutter focus:outline-none'>
        <Close className='w-10 lg:w-16' />
      </button>
    </motion.div>
  );
};

export const DonateModal = ({ donateOpen, toggleDonate }) => {
  return (
    <AnimatePresence exitBeforeEnter>
      {donateOpen && <NavInner key={donateOpen} toggleDonate={toggleDonate} />}
    </AnimatePresence>
  );
};
